import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MessageSquareTextIcon, SparklesIcon, ZapIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useSubscriptions, usageByThreadInfiniteQueryOptions } from "@/hooks/query/billing";
import { formatCreditReason } from "@/lib/credit-reason";
import { formatDate } from "@/lib/format";
import { noIndexSeo } from "@/lib/seo";
import { getPlanByName } from "@/lib/subscriptions/subscriptions";
import * as m from "@/paraglide/messages";
import { $getUserBalance } from "@/server/actions/billing";

export const Route = createFileRoute("/(app)/settings/usage")({
	component: RouteComponent,
	head: ({ match }) =>
		noIndexSeo({
			title: `${m.usage_title()} | StudyOS`,
			description: m.usage_description(),
			pathname: match.pathname,
		}),
});

function RouteComponent() {
	return (
		<div className="flex h-full flex-col">
			<div className="shrink-0 space-y-6 pb-4 px-2">
				<div>
					<h3 className="text-lg font-medium">{m.usage_title()}</h3>
					<p className="text-sm text-muted-foreground">{m.usage_description()}</p>
				</div>
				<Separator />
				<BalanceSummary />
			</div>
			<div className="min-h-0 flex-1 overflow-y-auto p-2">
				<UsageLedgerTable />
			</div>
		</div>
	);
}

function BalanceSummary() {
	const { data: balance, isPending: balPending } = useQuery({
		queryKey: ["billing", "balance"],
		queryFn: () => $getUserBalance(),
	});

	const { data: subscriptions, isPending: subPending } = useSubscriptions();

	const activeSub = subscriptions?.find((s) => s.status === "active" || s.status === "trialing");
	const currentPlan = getPlanByName(activeSub?.plan ?? "free")!;
	const isFree = !activeSub;

	return (
		<div className="flex items-center justify-between rounded-md border px-4 py-2.5">
			<div className="flex items-center gap-3">
				<SparklesIcon className="size-4 text-amber-500" />
				<div className="flex items-center gap-2">
					<span className="text-sm text-muted-foreground">
						{m.userDropdown_credits()}
					</span>
					{balPending ? (
						<Skeleton className="h-5 w-12" />
					) : (
						<span className="text-base font-semibold tabular-nums">
							{(balance ?? 0).toLocaleString()}
						</span>
					)}
				</div>
			</div>
			<div className="flex items-center gap-2">
				{subPending ? (
					<Skeleton className="h-5 w-16" />
				) : isFree ? (
					<Button
						variant="default"
						size="sm"
						render={
							<Link to="/settings/subscription">
								<ZapIcon className="size-3.5 mr-1" />
								{m.userDropdown_upgrade()}
							</Link>
						}
					/>
				) : (
					<Badge variant="secondary" className="gap-1">
						<ZapIcon className="size-3" />
						{currentPlan?.label ?? "Pro"}
					</Badge>
				)}
			</div>
		</div>
	);
}

function UsageLedgerTable() {
	const { data, isPending, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery(
		usageByThreadInfiniteQueryOptions(),
	);

	const rows = data?.pages.flatMap((p) => p.data) ?? [];

	if (isPending) {
		return <Skeleton className="h-48 w-full rounded-lg" />;
	}

	if (rows.length === 0) {
		return (
			<Card>
				<CardContent className="flex flex-col items-center gap-2 py-8">
					<MessageSquareTextIcon className="size-8 text-muted-foreground" />
					<p className="text-sm font-medium">{m.usage_noUsage()}</p>
					<p className="text-xs text-muted-foreground">{m.usage_noUsageDescription()}</p>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardContent className="p-0">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>{m.usage_thread()}</TableHead>
							<TableHead className="text-right">{m.usage_lastUsed()}</TableHead>
							<TableHead className="text-right">{m.usage_credits()}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{rows.map((row, i) => (
							<TableRow key={row.threadId ?? `grant-${i}`}>
								<TableCell>
									{row.kind === "thread" && row.threadId ? (
										<Link
											to="/chat/$threadId"
											params={{
												threadId: row.threadId,
											}}
											className="text-sm hover:underline"
										>
											{row.threadTitle || m.usage_untitledThread()}
										</Link>
									) : (
										<span className="text-sm">
											{formatCreditReason(row.reason) ||
												m.usage_untitledThread()}
										</span>
									)}
								</TableCell>
								<TableCell className="text-right text-sm text-muted-foreground">
									{formatDate(row.date)}
								</TableCell>
								<TableCell className="text-right tabular-nums font-medium">
									<span className={row.amount > 0 ? "text-emerald-600" : ""}>
										{row.amount > 0 ? "+" : ""}
										{row.amount.toLocaleString()}
									</span>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
				{hasNextPage && (
					<div className="flex justify-center py-3 border-t">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => fetchNextPage()}
							disabled={isFetchingNextPage}
						>
							{m.usage_loadMore()}
						</Button>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
